import React, { createContext, useRef, useState, useCallback } from "react";
import NotificationContainer from "../components/Notifications/NotificationContainer";

export const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const lastNotificationRef = useRef({
    signature: "",
    timestamp: 0,
    id: null,
  });

  const addNotification = useCallback((type, message, options = {}) => {
    const details = options.details || "";
    const signature = `${type}::${message}::${details}`;
    const now = Date.now();
    const last = lastNotificationRef.current;

    if (last.signature === signature && now - last.timestamp < 700) {
      return last.id;
    }

    const id = Date.now() + Math.random();
    const notification = {
      id,
      type,
      message,
      details,
      duration: options.duration !== undefined ? options.duration : 5000,
      action: options.action,
    };

    lastNotificationRef.current = {
      signature,
      timestamp: now,
      id,
    };

    setNotifications((prev) => [...prev, notification]);

    // Auto-dismiss if duration is set
    if (notification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.duration);
    }

    return id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showSuccess = useCallback(
    (message, options) => {
      return addNotification("success", message, options);
    },
    [addNotification],
  );

  const showError = useCallback(
    (message, options) => {
      return addNotification("error", message, options);
    },
    [addNotification],
  );

  const showWarning = useCallback(
    (message, options) => {
      return addNotification("warning", message, options);
    },
    [addNotification],
  );

  const showInfo = useCallback(
    (message, options) => {
      return addNotification("info", message, options);
    },
    [addNotification],
  );

  const value = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
    </NotificationContext.Provider>
  );
};
